import {
    api,
    apiErrorMessage,
    businessTimezone,
    formatMonthYear,
    getTimezone,
    todayInTz,
    useAuthStore,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    BusinessDayLabel,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    DateTime,
    ListTable,
    Money,
    MonthField,
    ObjectRef,
    PageHeader,
    RetryIcon,
    RowActions,
    StatusBadge,
    type ListColumnDef,
} from '@bemor/web-kit/ui';
import { useState } from 'react';

import type { TaxTotals, TaxTransaction } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';
import { ADMIN_STATUSES } from '@/lib/statuses';
import { TaxRateStoreCard } from '@/pages/money/tax-rate-store';

const PATH = '/staff/tax/transactions';
const PAGE_KEY = 'tax-transactions';

export const taxPrefetch = adminListPrefetch(PAGE_KEY, PATH);

const taxFilters = filterDefs(PATH, {
    q: 'Search',
    state: 'State',
    kind: 'Kind',
    region: 'Buyer state',
    merchant_id: 'Merchant',
    range: 'Created',
});

// BM-143: the current month is the tenant's business month (the totals are cut on it)
const currentPeriod = () => todayInTz(businessTimezone()).slice(0, 7);

/** The period totals per buyer state. */
const REGION_COLUMNS: ListColumnDef<TaxTotals['by_region'][number]>[] = [
    {
        key: 'region',
        label: 'Buyer state',
        cell: (row) => row.region || '—',
        locked: 'start',
    },
    {
        key: 'collected',
        label: 'Collected',
        cell: (row) => <Money cents={row.collected_cents} currency="USD" />,
        noTruncate: true,
    },
    {
        key: 'reversed',
        label: 'Reversed',
        cell: (row) => <Money cents={row.reversed_cents} currency="USD" />,
        noTruncate: true,
    },
    {
        key: 'net',
        label: 'Net',
        cell: (row) => <Money cents={row.net_cents} currency="USD" />,
        noTruncate: true,
    },
    {
        key: 'orders',
        label: 'Orders',
        cell: (row) => row.order_count,
        noTruncate: true,
    },
];

/**
 * BM-63 2.5: the tenant's tax provider transactions (the sale committed on settlement, one negative transaction per refund
 * and per lost chargeback) with the retry of a refused one, and the period totals per buyer state.
 */
export function TaxPage() {
    const can = useAuthStore((s) => s.can);
    const [period, setPeriod] = useState(currentPeriod());
    const [error, setError] = useState<string | null>(null);
    const totals = useOne<TaxTotals>(
        /^\d{4}-(0[1-9]|1[0-2])$/.test(period)
            ? `/staff/tax/totals?period=${period}`
            : null,
    );
    const retry = useWrite(
        async (id: string) =>
            (await api().post<TaxTransaction>(`${PATH}/${id}/retry`)).data,
        [PATH, '/staff/tax/totals'],
    );
    const columns: ListColumnDef<TaxTransaction>[] = [
        {
            key: 'id',
            label: 'Transaction',
            cell: (r) => <span className="font-mono text-xs">{r.id}</span>,
            csv: (r) => r.id,
            boardCard: true,
            locked: 'start',
            noTruncate: true,
        },
        {
            key: 'order_public_id',
            aliases: ['order'],
            label: 'Order',
            csv: (r) => r.order_public_id,
            cell: (r) => (
                <span className="font-mono text-xs">{r.order_public_id}</span>
            ),
        },
        {
            key: 'kind',
            label: 'Kind',
            sortKey: 'kind',
            csv: (r) => r.kind,
            cell: (r) => (
                <StatusBadge status={r.kind} domain={ADMIN_STATUSES.taxKind} />
            ),
        },
        {
            key: 'merchant_id',
            aliases: ['merchant'],
            label: 'Merchant',
            sortKey: 'merchant_id',
            csv: (r) => r.merchant_id,
            cell: (r) => <ObjectRef id={r.merchant_id} />,
        },
        {
            key: 'region',
            label: 'Buyer state',
            csv: (r) => r.region ?? '',
            cell: (r) => r.region ?? '—',
        },
        {
            key: 'pre_tax_cents',
            aliases: ['pre_tax'],
            label: 'Pre-tax',
            sortKey: 'pre_tax_cents',
            csv: (r) => r.pre_tax_cents,
            cell: (r) => <Money cents={r.pre_tax_cents} currency="USD" />,
            noTruncate: true,
        },
        {
            key: 'tax_cents',
            aliases: ['tax'],
            label: 'Tax',
            sortKey: 'tax_cents',
            csv: (r) => r.tax_cents,
            boardCard: true,
            cell: (r) => <Money cents={r.tax_cents} currency="USD" />,
            noTruncate: true,
        },
        {
            key: 'collected_tax_cents',
            aliases: ['collected'],
            label: 'Collected',
            csv: (r) => r.collected_tax_cents,
            cell: (r) => <Money cents={r.collected_tax_cents} currency="USD" />,
            noTruncate: true,
        },
        {
            key: 'state',
            label: 'State',
            sortKey: 'state',
            csv: (r) => r.state,
            cell: (r) => (
                <StatusBadge
                    status={r.state}
                    domain={ADMIN_STATUSES.taxTransaction}
                />
            ),
        },
        {
            key: 'error_code',
            aliases: ['error'],
            label: 'Provider said',
            csv: (r) => r.error_code ?? '',
            cell: (r) => r.error_code ?? '',
        },
        {
            key: 'attempts',
            label: 'Attempts',
            csv: (r) => r.attempts,
            cell: (r) => r.attempts,
            noTruncate: true,
        },
        {
            key: 'committed_at',
            aliases: ['committed'],
            label: 'Committed',
            kind: 'datetime',
            sortKey: 'committed_at',
            csv: (r) => r.committed_at ?? '',
            cell: (r) =>
                r.committed_at ? (
                    <DateTime value={r.committed_at} empty="—" />
                ) : (
                    '—'
                ),
            noTruncate: true,
        },
        {
            key: 'created_at',
            aliases: ['created'],
            label: 'Created',
            kind: 'datetime',
            sortKey: 'created_at',
            csv: (r) => r.created_at,
            cell: (r) => <DateTime value={r.created_at} empty="—" />,
            noTruncate: true,
        },
        {
            key: 'actions',
            label: 'Actions',
            actions: true,
            // BM-84 (rule G.6, design 0.7.0 tax): Retry in the row menu
            cell: (r) => (
                <RowActions
                    name={r.id}
                    data-testid={`tax-retry-${r.id}`}
                    items={[
                        {
                            label: 'Retry',
                            icon: RetryIcon,
                            hidden:
                                r.state === 'committed' || !can(P.taxManage),
                            disabled: retry.isPending,
                            onSelect: () => {
                                setError(null);
                                retry.mutate(r.id, {
                                    onError: (err) =>
                                        setError(apiErrorMessage(err)),
                                });
                            },
                        },
                    ]}
                />
            ),
        },
    ];
    const { state, query } = useAdminList<TaxTransaction>(PAGE_KEY, PATH, {
        columns,
        filters: taxFilters,
    });
    const t = totals.data?.data;
    return (
        <div>
            <PageHeader
                title="Sales tax"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {
                                'What was told to the tax provider: each sale once its charge settled, one negative transaction per refund and per lost chargeback. Anrok files and remits; the remittance is booked in Ledger.'
                            }
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            {error ? (
                <p role="alert" className="text-destructive mb-3 text-sm">
                    {error}
                </p>
            ) : null}
            <Card className="mb-4" data-testid="tax-totals">
                <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-col gap-1">
                        <CardTitle className="text-sm font-medium">
                            Period totals
                        </CardTitle>
                        {/* BM-143: the month is the tenant's business month, named */}
                        <BusinessDayLabel
                            zone={t?.zone ?? businessTimezone()}
                            viewerZone={getTimezone()}
                            prefix={formatMonthYear(period)}
                        />
                    </div>
                    {/* BM-84 (rule 0.1, design 0.7.0 tax): the period is a MonthField */}
                    <div className="w-full md:w-48">
                        <MonthField
                            aria-label="Period"
                            value={period}
                            onChange={(m) => setPeriod(m ?? '')}
                        />
                    </div>
                </CardHeader>
                <CardContent>
                    {t ? (
                        <div className="space-y-3 text-sm">
                            <div className="flex flex-wrap gap-6">
                                <span>
                                    Collected{' '}
                                    <Money
                                        cents={t.collected_cents}
                                        currency="USD"
                                    />
                                </span>
                                <span>
                                    Reversed{' '}
                                    <Money
                                        cents={t.reversed_cents}
                                        currency="USD"
                                    />
                                </span>
                                <span>
                                    Net{' '}
                                    <Money cents={t.net_cents} currency="USD" />
                                </span>
                                <span>{t.order_count} orders</span>
                                {t.pending_count ? (
                                    <span className="text-warning-foreground">
                                        {t.pending_count} not committed yet
                                    </span>
                                ) : null}
                            </div>
                            {/* BM-84 (rule 0.1): the per-state totals through the kit's table */}
                            <ListTable
                                columns={REGION_COLUMNS}
                                rows={[...t.by_region]}
                                rowKey={(row) => row.region || 'none'}
                                stickyHeader={false}
                                emptyState={
                                    <p className="text-muted-foreground p-4 text-center text-sm">
                                        Nothing committed in this period.
                                    </p>
                                }
                            />
                        </div>
                    ) : (
                        <p className="text-muted-foreground text-sm">
                            {totals.isLoading ? 'Loading…' : 'Enter a period.'}
                        </p>
                    )}
                </CardContent>
            </Card>
            <TaxRateStoreCard />
            <AdminListPage
                noun="transactions"
                state={state}
                query={query}
                rowKey={(r) => r.id}
                controls={{
                    state: { domain: ADMIN_STATUSES.taxTransaction },
                    kind: { domain: ADMIN_STATUSES.taxKind },
                }}
            />
        </div>
    );
}
